<template>
  <div v-if="hasAttributes" class="pdp-product-attributes-container">
    <component
      :is="getAttributeComponent(attributes)"
      :base-properties="baseProperties"
      :product="product"
      :init-attribute-selection="initAttributeSelection"
      :render-stock-status="renderStockStatus"
      :stock-message="stockMessage"
      :label="getAttributeLabel(attributes)"
      :attribute="attributes"
      @product-selected="productSelected"
      @color-changed="
        (color) => {
          $emit('color-changed', color);
        }
      "
    >
    </component>
    <size-link v-if="isSizeLinkEnabled" :size-link-guide="product.sizeLinkGuide" :base-properties="baseProperties" />
  </div>
</template>
<script>
import {AttributeRenderer} from '@/components/containers/pdp/attributes/mixins/attribute-renderer';
import Variant from '@/utils/variant-helpers.js';
import cloneDeep from 'lodash/cloneDeep';
import AttributeHelper from '@/utils/attribute-helpers.js';
import SizeLink from '@/components/containers/pdp/attributes/SizeLink';
import {mapState, mapGetters} from 'vuex';

export default {
  name: 'PdpProductAttributesContainer',
  components: {SizeLink},
  mixins: [AttributeRenderer],
  props: {
    product: {
      type: Object,
      required: true,
    },
    initAttributeSelection: {
      type: Object,
      default: () => {},
    },
    value: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters('backend', ['b2b']),
    hasAttributes() {
      return this.product && this.attributes && this.attributes.length > 0;
    },
    hasSizeAttribute() {
      const attributeArray = AttributeHelper(this.attributes).flatten();
      return attributeArray.some((attribute) => attribute.code?.toLocaleLowerCase().includes('size'));
    },
    isSizeLinkEnabled() {
      return this.hasSizeAttribute && (this.product?.sizeLinkGuide || this.sizeChartData);
    },
    attributes() {
      return this.getAttributes(this.product.categorizationHierarchyValues);
    },
    ...mapState('pdp', ['sizeChartData']),
  },
  methods: {
    productSelected(options) {
      this.$emit('input', options.productCode);
    },
    getFilteredAttributes(categorizationHierarchyValues) {
      const saleCategorizationValues = AttributeHelper(categorizationHierarchyValues).map((category) => {
        if (Variant(category.options.productCode, this.product, this.b2b).getIsOnSale()) {
          category.isSale = true;
        }
      });
      let filteredAttributes = cloneDeep(saleCategorizationValues);
      saleCategorizationValues.forEach((item) => {
        item.children.forEach((child) => {
          if (!child.isSale) return;
          const existingSale = filteredAttributes.find((attribute) => {
            return attribute.isSale && item.value === attribute.value;
          });
          if (existingSale) {
            existingSale.children = [...existingSale.children, child];
          } else {
            //create a new root
            filteredAttributes.push({...item, children: [child], isSale: true});
          }
        });
      });
      return filteredAttributes;
    },
    getAttributes(categorizationHierarchyValues) {
      if (!categorizationHierarchyValues) return [];
      let filteredAttributes = this.getFilteredAttributes(categorizationHierarchyValues);
      // filter sale children from non-sale attributes
      filteredAttributes = filteredAttributes
        .map((attribute) => {
          if (attribute.isSale) return attribute;
          attribute.children = attribute.children.filter((child) => !child.isSale);
          return attribute;
        })
        //remove duplicate roots if they have no children
        .filter((attribute) => {
          //find duplicate (there should only ever be duplicates with alternate isSale flags)
          const existingAttr = filteredAttributes.find((attr) => {
            return attr.value === attribute.value && attr.isSale !== attribute?.isSale;
          });
          if (existingAttr) {
            return attribute?.children?.length;
          } else {
            return true;
          }
        });
      return filteredAttributes;
    },
  },
};
</script>
